/**
 * Helpers puros de JWT — únicos para marketdata e ordersRouter, que mantinham
 * cópias idênticas (unificadas na modularização dos god-objects; equivalência
 * provada em __tests__/authTokenContract.test.js ANTES da unificação).
 * refreshToken/getValidToken NÃO vivem aqui: têm estado e endpoints próprios
 * em cada serviço.
 */

/**
 * Parse JWT token to extract expiration date
 * @param {string} token - JWT token
 * @returns {Date|null} - Expiration date or null if invalid/unparseable
 * @note API tokens expire after 30 minutes (as of 2025-12-12)
 */
export const parseTokenExpiration = (token) => {
  try {
    if (!token) return null;

    // JWT tokens have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Decode the payload (second part)
    const payload = parts[1];

    // Add padding if needed for base64 decoding
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);

    // Decode base64
    const decodedPayload = atob(paddedPayload);
    const parsedPayload = JSON.parse(decodedPayload);

    // Extract expiration (exp field is in seconds since epoch)
    if (parsedPayload.exp) {
      return new Date(parsedPayload.exp * 1000); // Convert to milliseconds
    }

    return null;
  } catch (error) {
    console.warn("Failed to parse JWT token expiration:", error);
    return null;
  }
};

/**
 * Check if JWT token is expired or will expire within buffer time
 * @param {string} token - JWT token
 * @param {number} bufferMinutes - Minutes before expiration to consider expired (default: 5)
 * @returns {boolean} - True if token is expired or about to expire (fail-closed)
 */
export const isTokenExpired = (token, bufferMinutes = 5) => {
  const expirationDate = parseTokenExpiration(token);
  if (!expirationDate) {
    // If we can't parse expiration, consider it expired to be safe
    return true;
  }

  const now = new Date();
  const bufferMs = bufferMinutes * 60 * 1000; // Convert to milliseconds
  const expirationWithBuffer = new Date(expirationDate.getTime() - bufferMs);

  return now >= expirationWithBuffer;
};
